import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { DataTable, type Column } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { api } from '@/lib/api';
import { formatAgo } from '@/lib/ago';
import { formatDuration } from '@/lib/format';
import { LevelPill } from '@/components/apps/LevelPill';
import { useAdaptivePolledData } from '@/lib/hooks/useAdaptivePolledData';
import { usePolledData } from '@/lib/hooks/usePolledData';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';
import type { Deployment, DeploymentLog } from '@/types/deployment';

const fetchDeployments = () => api.deployments.list();
const fetchEndpointNames = async (): Promise<Record<number, string>> => {
  const endpoints = await api.endpoints.listAll();
  return Object.fromEntries(endpoints.map((e) => [e.id, e.name]));
};

const FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
  { value: 'rolled_back', label: 'Rolled back' },
];

const parseTime = (value: string | null | undefined) => {
  const t = value ? Date.parse(value) : NaN;
  return Number.isNaN(t) ? null : t;
};

const durationMs = (d: Deployment) => {
  const start = parseTime(d.created_at);
  const end = parseTime(d.completed_at);
  return start === null || end === null ? null : end - start;
};

const hasActive = (list: Deployment[] | undefined) =>
  list?.some((d) => d.status === 'pending' || d.status === 'in_progress') ?? false;

interface LogsState {
  logs: DeploymentLog[];
  loading: boolean;
  error: string | null;
}

export function DeploymentList() {
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState<Deployment | null>(null); // kept after close so the title doesn't flicker
  const [logsOpen, setLogsOpen] = useState(false);
  const { captureOpener, restoreFocus } = useDialogFocusRestore();
  const [logsState, setLogsState] = useState<LogsState>({ logs: [], loading: false, error: null });
  const logsRequest = useRef(0);

  const { data: deployments, error, loading, refetch } = useAdaptivePolledData(
    fetchDeployments,
    (list) => (hasActive(list) ? 10_000 : 30_000),
    30_000,
  );
  const { data: endpointNames } = usePolledData(fetchEndpointNames, undefined);

  const openLogs = async (deployment: Deployment) => {
    const reqId = ++logsRequest.current;
    setSelected(deployment);
    setLogsOpen(true);
    setLogsState({ logs: [], loading: true, error: null });
    try {
      const logs = await api.deployments.getLogs(deployment.id);
      if (reqId === logsRequest.current) setLogsState({ logs, loading: false, error: null });
    } catch (e) {
      if (reqId === logsRequest.current) {
        setLogsState({ logs: [], loading: false, error: e instanceof Error ? e.message : 'Failed to load logs' });
      }
    }
  };

  const closeLogs = () => {
    logsRequest.current++; // invalidate any in-flight response
    setLogsOpen(false);
  };

  const endpointName = (d: Deployment) => endpointNames?.[d.endpoint_id];

  const columns: Column<Deployment>[] = [
    {
      key: 'id',
      header: 'ID',
      sortValue: (d) => d.id,
      cell: (d) => <span className="font-mono text-xs">#{d.id}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (d) => d.status,
      cell: (d) => <StatusBadge status={d.status} />,
    },
    { key: 'type', header: 'Type', sortValue: (d) => d.type, cell: (d) => <span className="capitalize">{d.type}</span> },
    {
      key: 'endpoint',
      header: 'Endpoint',
      sortValue: (d) => endpointName(d)?.toLowerCase() ?? null,
      cell: (d) => endpointName(d) ?? '—',
    },
    {
      key: 'created',
      header: 'Created',
      sortValue: (d) => parseTime(d.created_at),
      cell: (d) => {
        const t = parseTime(d.created_at);
        if (t === null) return '—';
        return (
          <span className="whitespace-nowrap text-muted-foreground" title={new Date(t).toLocaleString()}>
            {formatAgo(t)}
          </span>
        );
      },
    },
    {
      key: 'duration',
      header: 'Duration',
      sortValue: durationMs,
      cell: (d) => <span className="tabular-nums whitespace-nowrap">{formatDuration(durationMs(d))}</span>,
    },
    {
      key: 'logs',
      header: 'Logs',
      className: 'text-right',
      cell: (d) => (
        <Button
          variant="ghost"
          size="sm"
          aria-label={`View logs for deployment #${d.id}`}
          onClick={() => void openLogs(d)}
        >
          Logs
        </Button>
      ),
    },
  ];

  const rows = deployments && statusFilter ? deployments.filter((d) => d.status === statusFilter) : deployments;

  const empty =
    deployments && deployments.length > 0
      ? {
          title: 'No deployments match this filter',
          action: (
            <Button variant="outline" size="sm" onClick={() => setStatusFilter('')}>
              Clear filter
            </Button>
          ),
        }
      : { title: 'No deployments yet' };

  const hasData = deployments !== undefined;

  return (
    <div className="space-y-3">
      {hasData && error !== null && (
        <div
          role="status"
          className="flex items-center justify-between gap-2 rounded bg-status-warning-soft px-2 py-1 text-xs text-status-warning"
        >
          <span>Showing last known data — refresh failed.</span>
          <Button variant="ghost" size="xs" onClick={refetch}>
            Retry
          </Button>
        </div>
      )}
      <DataTable<Deployment>
        columns={columns}
        rows={rows}
        getRowKey={(d) => d.id}
        searchText={(d) => [d.id, d.status, d.type, endpointName(d)].filter(Boolean).join(' ')}
        searchPlaceholder="Search deployments…"
        loading={loading}
        error={hasData ? null : error}
        onRetry={refetch}
        empty={empty}
        onRowClick={(d) => void openLogs(d)}
        pageSize={20}
        pageResetKey={statusFilter}
        toolbar={
          <>
            <select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            {rows && (
              <span className="text-sm text-muted-foreground" aria-live="polite">
                {rows.length} {rows.length === 1 ? 'deployment' : 'deployments'}
              </span>
            )}
          </>
        }
      />

      <Dialog open={logsOpen} onOpenChange={(open) => !open && closeLogs()}>
        <DialogContent className="max-w-3xl" onOpenAutoFocus={() => captureOpener()} onCloseAutoFocus={restoreFocus}>
          <DialogHeader>
            <DialogTitle>Deployment #{selected?.id}</DialogTitle>
            <DialogDescription asChild>
              <div className="flex items-center gap-2">
                <StatusBadge status={selected?.status} />
                <span className="capitalize">{selected?.type}</span>
              </div>
            </DialogDescription>
          </DialogHeader>
          {logsState.loading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : logsState.error ? (
            <div role="alert" className="rounded-md bg-status-danger-soft px-3 py-2 text-sm text-status-danger">
              <span>{logsState.error}</span>
              {selected && (
                <Button variant="ghost" size="xs" className="ml-2" onClick={() => void openLogs(selected)}>
                  Retry
                </Button>
              )}
            </div>
          ) : logsState.logs.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">No logs</div>
          ) : (
            <div
              tabIndex={0}
              aria-label="Deployment log"
              className="max-h-[60vh] space-y-1 overflow-auto rounded-md border bg-muted/40 p-2 outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {logsState.logs.map((log) => {
                const t = parseTime(log.timestamp);
                return (
                  <div key={log.id} className="flex items-start gap-2 text-sm">
                    <span className="shrink-0 pt-0.5 font-mono text-xs text-muted-foreground">
                      {t === null ? '—' : new Date(t).toLocaleString()}
                    </span>
                    <LevelPill level={log.level} />
                    <span className="min-w-0 flex-1 break-words">{log.message}</span>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
